import React from "react";
import { Award, Medal, Trophy, Loader2, Inbox } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

export const STATUS_META = {
  pending_validation: { label: "En attente de validation", cls: "bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30", dot: "bg-amber-500" },
  inactive: { label: "Membre inactif", cls: "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30", dot: "bg-slate-400" },
  active: { label: "Membre actif", cls: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30", dot: "bg-emerald-600" },
  sanctioned: { label: "Sanctionné", cls: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30", dot: "bg-rose-600" },
  banned: { label: "Banni", cls: "bg-red-50 text-red-800 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30", dot: "bg-red-600" },
};

export const ROLE_LABEL = { admin: "Administration", coach: "Entraîneur", member: "Membre" };

export const StatusBadge = ({ status, className }) => {
  const m = STATUS_META[status] || STATUS_META.inactive;
  return (
    <span data-testid="status-badge"
      className={cn("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium", m.cls, className)}>
      <span className={cn("h-2 w-2 rounded-full", m.dot)} />
      {m.label}
    </span>
  );
};

const SPORT_CHIP = {
  emerald: "bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30",
  orange: "bg-orange-50 text-orange-900 border-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/30",
  sky: "bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30",
  indigo: "bg-indigo-50 text-indigo-900 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/30",
  slate: "bg-slate-50 text-slate-900 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30",
  rose: "bg-rose-50 text-rose-900 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30",
};

export const SportChip = ({ name, color = "slate", className }) => (
  <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium", SPORT_CHIP[color] || SPORT_CHIP.slate, className)}>
    {name}
  </span>
);

export const Money = ({ value, className }) => (
  <span className={cn("tabular-nums font-semibold", className)}>
    {Number(value ?? 0).toFixed(2)} $
  </span>
);

export const UserAvatar = ({ user, size = "h-10 w-10", className }) => {
  const initials = user ? `${(user.first_name || "?")[0] || ""}${(user.last_name || "")[0] || ""}` : "?";
  return (
    <Avatar className={cn(size, className)}>
      {user?.photo ? <AvatarImage src={user.photo} alt={initials} /> : null}
      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
        {initials.toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
};

export const TrophyIcon = ({ kind = "trophy", className }) => {
  if (kind === "medal") return <Medal className={className} />;
  if (kind === "award") return <Award className={className} />;
  return <Trophy className={className} />;
};

export const Loading = ({ label = "Chargement…" }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid="loading">
    <Loader2 className="h-5 w-5 animate-spin" />
    <span className="text-sm">{label}</span>
  </div>
);

export const EmptyState = ({ title = "Rien à afficher", description = "", icon: Icon = Inbox, action = null }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-card py-14 px-6 text-center">
    <div className="mb-3 rounded-full bg-muted p-3"><Icon className="h-6 w-6 text-muted-foreground" /></div>
    <p className="font-heading font-semibold text-foreground">{title}</p>
    {description ? <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p> : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between gap-3">
    <h2 className="text-lg sm:text-xl font-heading font-semibold">{children}</h2>
    {action}
  </div>
);

export const PageContainer = ({ children, className }) => (
  <div className={cn("mx-auto w-full max-w-6xl px-4 sm:px-6 py-6 space-y-6 sm:space-y-8", className)}>{children}</div>
);

export const Logo = ({ size = 40, withText = true, light = false }) => (
  <div className="flex items-center gap-2.5 notranslate" translate="no">
    <img src="/asl-logo.jpeg" alt="ASL — Association Sports et Loisirs Pierre-De-Saurel" style={{ width: size, height: size }}
         className="rounded-lg object-cover shadow-sm" />
    {withText && (
      <div className="leading-tight">
        <div className={cn("font-heading font-bold text-sm", light ? "text-white" : "text-primary")}>ASL</div>
        <div className={cn("text-[10px]", light ? "text-white/70" : "text-muted-foreground")}>Pierre-De-Saurel</div>
      </div>
    )}
  </div>
);

export const SESSION_LABEL = { ete: "Été", hiver: "Hiver", ponctuel: "Ponctuel" };
